/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Table, Button, Form, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cloneDeep, filter, forEach, isEmpty, isEqual, map } from 'lodash';

import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import * as videoOps from '../../../api/Videos/video-operations';

import methodPromise from '../../../modules/method-promise-wrapper';

import PageHeader from '../../atoms/PageHeader';
import EmptyTableAlert from '../../components/EmptyTableAlert';
import ItemList from '../../components/ItemList';
import List from '../../components/List';
import TagInput from '../../components/TagInput';
import Trash from '../../components/Trash';

const NAV_HEIGHT = 56; // pixels
const FOOTER_HEIGHT = 60; // pixels
const NEW_TEMPLATE_BUTTON_ROW_HEIGHT = 51; // pixels
const TAGS_FILTER_ROW_SINGLE_HEIGHT = 46; // pixels
const PREV_AND_NEXT_BUTTONS_HEIGHT = 85; // pixels
const FIXED_HEIGHT =
  NAV_HEIGHT + FOOTER_HEIGHT + NEW_TEMPLATE_BUTTON_ROW_HEIGHT + TAGS_FILTER_ROW_SINGLE_HEIGHT;

const TemplateList = ({ items, ...rest }) => {
  const { isVideoEditor, selectedItems } = rest;
  if (isVideoEditor) {
    const selectedListItems = filter(items, item => selectedItems.has(item._id));
    const nonSelectedListItems = filter(items, item => !selectedItems.has(item._id));
    return (
      <>
        <List items={selectedListItems} {...rest} />
        <List items={nonSelectedListItems} {...rest} />
      </>
    );
  }
  return <List items={items} {...rest} />;
};
TemplateList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired
};

class AnnotationTemplates extends Component {
  selectedAnnotationTemplates = new Set();

  state = {
    trashShow: false,
    tagsInFilter: [],
    suggestions: [],
    height: 0,
    allSelected: false
  };

  componentDidMount() {
    this.updateSuggestions();
    this.updateWindowDimensions();
    this.loadVideoAnnotationTemplates();
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentDidUpdate(prevProps) {
    const { annotationTemplates } = this.props;
    const { annotationTemplates: prevAnnotationTemplates } = prevProps;
    if (!isEqual(prevAnnotationTemplates, annotationTemplates)) {
      this.updateSuggestions();
    }
  }

  componentWillUnmount() {
    this.selectedAnnotationTemplates = null;
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  getSelectedAnnotationTemplates() {
    const { annotationTemplates } = this.props;
    return map(
      filter(annotationTemplates, annotationTemplate =>
        this.selectedAnnotationTemplates.has(templateOps.getId(annotationTemplate))
      ),
      selectedAnnotationTemplate => {
        const clonedAnnotation = cloneDeep(selectedAnnotationTemplate);
        delete clonedAnnotation.deleted;
        return clonedAnnotation;
      }
    );
  }

  trashClose = () => this.setState({ trashShow: false });

  trashOpen = () => this.setState({ trashShow: true });

  handleTagDelete = tagIndex =>
    this.setState(prevState => {
      const newTags = prevState.tagsInFilter.filter((tag, index) => index !== tagIndex);
      return { tagsInFilter: newTags };
    });

  handleTagAddition = newTag =>
    this.setState(prevState => {
      const newTags = [...prevState.tagsInFilter, newTag];
      return { tagsInFilter: newTags };
    });

  updateWindowDimensions = () => this.setState({ height: window.innerHeight });

  handleSelect = annotationTemplateId => {
    const { annotationTemplates, updateVideoProperty } = this.props;
    if (!this.selectedAnnotationTemplates.has(annotationTemplateId)) {
      this.selectedAnnotationTemplates.add(annotationTemplateId);
      let allSelected = false;
      if (this.selectedAnnotationTemplates.size === annotationTemplates.length) allSelected = true;
      this.setState({ allSelected });
    } else if (this.selectedAnnotationTemplates.has(annotationTemplateId)) {
      this.selectedAnnotationTemplates.delete(annotationTemplateId);
      this.setState({ allSelected: false });
    }
    updateVideoProperty('annotationTemplates', this.getSelectedAnnotationTemplates());
  };

  handleSelectAll = () => {
    const { annotationTemplates, updateVideoProperty } = this.props;
    const { allSelected } = this.state;
    if (!allSelected) {
      forEach(annotationTemplates, annotationTemplate => {
        const annotationTemplateId = templateOps.getId(annotationTemplate);
        if (!this.selectedAnnotationTemplates.has(annotationTemplateId))
          this.selectedAnnotationTemplates.add(annotationTemplateId);
      });
      this.setState({ allSelected: true });
    } else if (allSelected) {
      forEach(annotationTemplates, annotationTemplate => {
        const annotationTemplateId = templateOps.getId(annotationTemplate);
        if (this.selectedAnnotationTemplates.has(annotationTemplateId))
          this.selectedAnnotationTemplates.delete(annotationTemplateId);
      });
      this.setState({ allSelected: false });
    }
    updateVideoProperty('annotationTemplates', this.getSelectedAnnotationTemplates());
  };

  async updateSuggestions() {
    const newSuggestions = await methodPromise('videos.getTagSuggestions');
    this.setState({ suggestions: newSuggestions });
  }

  loadVideoAnnotationTemplates() {
    const { updatedVideo } = this.props;
    const annotationTemplates = videoOps.getAnnotationTemplates(updatedVideo);
    forEach(annotationTemplates, annotationTemplate =>
      this.selectedAnnotationTemplates.add(templateOps.getId(annotationTemplate))
    );
  }

  render() {
    const {
      annotationTemplates,
      deletedAnnotationTemplates,
      totalCount,
      deletedCount,
      trashAnnotationTemplate,
      restoreAnnotationTemplate,
      deleteAnnotationTemplate,
      selectedTemplate,
      changeSelectedTemplate,
      match,
      history,
      isVideoEditor,
      sort,
      handleSort
    } = this.props;
    const { trashShow, tagsInFilter, suggestions, height, allSelected } = this.state;
    const tableHeight = !isVideoEditor
      ? height - FIXED_HEIGHT
      : height - FIXED_HEIGHT - PREV_AND_NEXT_BUTTONS_HEIGHT;
    const iconToDraw = allSelected ? 'check-circle' : 'circle';
    const listProps = {
      items: annotationTemplates,
      match,
      loading: false,
      history,
      deleteButton: !isVideoEditor,
      softDeleteItem: trashAnnotationTemplate,
      datesColumns: false,
      iconColumn: true,
      handleSelectRow: changeSelectedTemplate,
      itemIdentifier: 'id',
      selectedRow: selectedTemplate,
      clickAction: 'select',
      filterOptions: { tags: { tagsInFilter }, name: 'tags' },
      isVideoEditor,
      selectedItems: this.selectedAnnotationTemplates,
      selectColumn: isVideoEditor,
      selectItem: this.handleSelect
    };
    return (
      <>
        {!isVideoEditor && (
          <>
            <Trash
              isTrashOpen={trashShow}
              closeTrash={this.trashClose}
              itemName="Annotation Templates"
              deletedCount={deletedCount}
              restoreItem={restoreAnnotationTemplate}
              deleteItem={deleteAnnotationTemplate}
              deletedItems={deletedAnnotationTemplates}
            />
            <PageHeader>
              <div className="ml-auto">
                <Link className="btn btn-success" to={`${match.url}/new`}>
                  Add Template
                </Link>
              </div>
            </PageHeader>
          </>
        )}
        <Form.Group as={Row} className="align-items-center mb-0">
          <Form.Label column xs={3}>
            Filter by tags
          </Form.Label>
          <Col xs={9}>
            <TagInput
              tags={tagsInFilter}
              suggestions={suggestions}
              handleDelete={this.handleTagDelete}
              handleAddition={this.handleTagAddition}
            />
          </Col>
        </Form.Group>
        <ItemList height={tableHeight}>
          <Table responsive hover size="sm">
            <thead>
              <tr>
                {isVideoEditor && (
                  <th className="text-center align-middle">
                    <Button
                      id="toggleSelectedAll"
                      variant={allSelected ? 'success' : 'light'}
                      onClick={this.handleSelectAll}
                    >
                      <FontAwesomeIcon icon={['far', iconToDraw]} size="lg" />
                    </Button>
                  </th>
                )}
                <th className="align-middle" style={{ cursor: 'pointer' }} onClick={handleSort}>
                  Annotation Templates ({totalCount}){' '}
                  {sort && <FontAwesomeIcon icon="sort-up" className="ml-1" />}
                  {!sort && <FontAwesomeIcon icon="sort-down" className="ml-1" />}
                </th>
                <th className="text-center align-middle" />
                {!isVideoEditor && (
                  <th className="text-center align-middle">
                    <Button
                      id="recycle-bin-projects"
                      variant="light"
                      onClick={() => this.setState({ trashShow: true })}
                      block
                    >
                      <FontAwesomeIcon icon="trash-alt" />
                    </Button>
                  </th>
                )}
              </tr>
            </thead>
            {!isEmpty(annotationTemplates) ? (
              <TemplateList {...listProps} />
            ) : (
              <EmptyTableAlert numberOfColumns={6} message="No Templates yet!" />
            )}
          </Table>
        </ItemList>
      </>
    );
  }
}

AnnotationTemplates.propTypes = {
  annotationTemplates: PropTypes.arrayOf(PropTypes.object).isRequired,
  deletedAnnotationTemplates: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number.isRequired,
  deletedCount: PropTypes.number.isRequired,
  trashAnnotationTemplate: PropTypes.func.isRequired,
  restoreAnnotationTemplate: PropTypes.func.isRequired,
  deleteAnnotationTemplate: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  selectedTemplate: PropTypes.string.isRequired,
  changeSelectedTemplate: PropTypes.func.isRequired,
  isVideoEditor: PropTypes.bool.isRequired,
  updatedVideo: PropTypes.object.isRequired,
  updateVideoProperty: PropTypes.func.isRequired,
  handleSort: PropTypes.func.isRequired,
  sort: PropTypes.bool.isRequired
};

export default AnnotationTemplates;
